/**
 * "What's new" (Dr. John, 2026-10-28, like Dentrix): when Computers2Go
 * releases a module it writes a short announcement with a few steps. After
 * sign-in, the user's undismissed announcements open once per browser
 * session as a small walkthrough (Back / Next / Done). Done or "Don't show
 * again" dismisses it for this user; closing it brings it back next time.
 * The user menu's "What's new" reopens the recent ones at any time. Not
 * shown while C2G support is signed in as someone (their dismissals are
 * theirs to make). Mounted once by the shell.
 */
import { useNavigate } from '@tanstack/react-router';
import type { WhatsNewItem } from '@velora/contracts/feedback';
import { Button, Dialog, EmptyState } from '@velora/ui';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { registerUserMenuItem, useOptionalSession } from '../../app/shell.js';
import { dismissWhatsNew, useWhatsNew } from './whats-new-api.js';
import { routeTarget, shownKey, userIsBusy } from './whats-new-logic.js';
import { feedbackUi, useFeedbackUi } from './store.js';
import './feedback.css';

function markShown(userId: string): boolean {
  try {
    const key = shownKey(userId);
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
  } catch {
    // Private mode or blocked storage: show it, at most once per page load.
  }
  return true;
}

const BUSY_RETRY_MS = 2000;

export function WhatsNewHost() {
  const session = useOptionalSession();
  const { whatsNew } = useFeedbackUi();
  const userId = session?.user.id ?? null;
  const impersonated = Boolean(session?.user.impersonatedBy);
  const fresh = useWhatsNew('new', userId !== null && !impersonated);
  const all = useWhatsNew('all', userId !== null && whatsNew === 'all');

  useEffect(() => {
    if (!userId) return undefined;
    return registerUserMenuItem({
      id: 'feedback.whats-new',
      label: "What's new",
      order: 49,
      onSelect: () => feedbackUi.openWhatsNew('all'),
    });
  }, [userId]);

  // Once per sign-in (browser session), when there is something new. It waits
  // while the user is busy (QA r57: it opened over Ctrl+K mid-search, over the
  // medical-alert popup and over the phone install sheet).
  const count = fresh.data?.items.length ?? 0;
  useEffect(() => {
    if (!userId || count === 0 || feedbackUi.get().whatsNew !== null) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tryOpen = () => {
      if (feedbackUi.get().whatsNew !== null) return;
      if (userIsBusy()) {
        timer = setTimeout(tryOpen, BUSY_RETRY_MS);
        return;
      }
      if (markShown(userId)) feedbackUi.openWhatsNew('new');
    };
    tryOpen();
    return () => clearTimeout(timer);
  }, [userId, count]);

  if (!userId || whatsNew === null) return null;
  const query = whatsNew === 'all' ? all : fresh;
  return (
    <WhatsNewDialog
      // Remount per mode so each opening starts at the first step.
      key={whatsNew}
      items={query.data?.items ?? []}
      loading={query.isPending}
      onClose={() => {
        feedbackUi.closeWhatsNew();
        void fresh.refetch();
      }}
    />
  );
}

function WhatsNewDialog({
  items,
  loading,
  onClose,
}: {
  items: readonly WhatsNewItem[];
  loading: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [step, setStep] = useState(0);
  const item = items[index];
  const steps = item?.steps ?? [];
  const current = steps[step];
  const last = step >= steps.length - 1;

  const next = () => {
    if (index + 1 < items.length) {
      setIndex(index + 1);
      setStep(0);
    } else onClose();
  };
  const dismiss = () => {
    if (item && !item.dismissed) void dismissWhatsNew(item.id).catch(() => undefined);
    next();
  };

  const title = items.length > 1 ? `What's new (${index + 1} of ${items.length})` : "What's new";

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={title}
      {...(item ? { description: item.summary } : {})}
      size="sm"
      className="wn-dialog"
      footer={
        item ? (
          <>
            {item.dismissed ? null : (
              <Button variant="ghost" className="wn-dialog__skip" onClick={dismiss}>
                Don&apos;t show again
              </Button>
            )}
            <Button variant="secondary" disabled={step === 0} onClick={() => setStep(step - 1)}>
              Back
            </Button>
            {last ? (
              <Button variant="primary" onClick={dismiss}>
                Done
              </Button>
            ) : (
              <Button variant="primary" onClick={() => setStep(step + 1)}>
                Next
              </Button>
            )}
          </>
        ) : (
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {!item ? (
        loading ? (
          <p className="wn-dim">Loading...</p>
        ) : (
          <EmptyState
            icon={<Sparkles size={20} aria-hidden />}
            title="Nothing new right now"
            description="New features are listed here when they are released."
          />
        )
      ) : (
        <section className="wn-step" aria-live="polite">
          <p className="wn-step__count">
            {item.title} · Step {step + 1} of {steps.length}
          </p>
          {current ? (
            <>
              <h3 className="wn-step__title">{current.title}</h3>
              <p className="wn-step__body">{current.body}</p>
              {current.route ? (
                <Button
                  size="sm"
                  variant="ghost"
                  trailingIcon={<ArrowRight size={14} aria-hidden />}
                  onClick={() => {
                    const t = routeTarget(current.route ?? '/');
                    void navigate({ to: t.to, search: t.search } as never);
                    onClose();
                  }}
                >
                  Show me
                </Button>
              ) : null}
            </>
          ) : null}
          <ol className="wn-dots" aria-hidden>
            {steps.map((s, i) => (
              <li key={s.title} className={i === step ? 'is-current' : undefined} />
            ))}
          </ol>
        </section>
      )}
    </Dialog>
  );
}
